import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { adminUserProps, UpdatePasswordParams } from 'types/med'
import { Controller, useForm } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import validationSchema from 'ui/shorthands/validationSchema'
import { StackItem } from 'ui/layout/StackItem'
import { Button, Theme } from '@mui/material'
import Modal from 'ui/layout/Modal'
import InputPassword from 'ui/partials/InputPassword'
import { makeStyles } from 'tss-react/mui'
import Stack from 'ui/layout/Stack'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'

const useStyles = makeStyles()((theme: Theme) => ({
  passwordRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  maskedPassword: {
    letterSpacing: '0.5px',
  },
  modalItems: {
    padding: theme.spacing(1, 0),
  },
}))

export type AdminUserSectionProps = {
  adminUser: adminUserProps
  onSubmit: (params: UpdatePasswordParams) => Promise<void>
}

const AdminUserSection: React.FC<AdminUserSectionProps> = ({
  adminUser,
  onSubmit,
}: AdminUserSectionProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const [changePasswordModal, setChangePasswordModal] = useState<boolean>(false)

  const schema = yup.object().shape({
    old_password: validationSchema.textPassword(),
    new_password: validationSchema.textNewPassword('old_password'),
  })

  const {
    handleSubmit,
    control,
    trigger,
    watch,
    reset,
    formState: { dirtyFields, isValid, isSubmitting, errors },
    setError,
  } = useForm<FieldValues>({
    defaultValues: {
      old_password: '',
      new_password: '',
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const showChangePasswordModal = () => setChangePasswordModal(true)
  const hideChangePasswordModal = () => {
    setChangePasswordModal(false)
    reset()
  }
  const watchOldPassword = watch('new_password')

  // NOTE: 異なるパスワードを入力して下さい、が出たあとにold_passwordを変更してもエラーが残る問題の回避
  useEffect(() => {
    if (dirtyFields.new_password) {
      trigger('new_password')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchOldPassword])

  const submitDisabled = !isValid || isSubmitting

  return (
    <>
      <StackItem label={t('氏名')}>
        {adminUser.last_name}
        {adminUser.first_name}
      </StackItem>
      <StackItem label={t('氏名(ふりがな)')}>
        {adminUser.last_name_kana}
        {adminUser.first_name_kana}
      </StackItem>
      <StackItem label={t('メールアドレス')}>{adminUser.email}</StackItem>
      <StackItem label={t('パスワード')}>
        <div className={classes.passwordRow}>
          <span className={classes.maskedPassword}>******</span>
          <Button color="primary" onClick={showChangePasswordModal}>
            {t('変更')}
          </Button>
        </div>
      </StackItem>
      <Modal
        large
        handleClose={hideChangePasswordModal}
        open={changePasswordModal}
      >
        <div className={classes.modalItems}>
          <StackItem label={t('現在のパスワード')}>
            <Controller
              name={'old_password'}
              control={control}
              render={({ field }) => (
                <InputPassword
                  {...field}
                  error={!!errors?.old_password}
                  helperText={errors?.old_password?.message}
                  fullWidth
                />
              )}
            />
          </StackItem>
          <StackItem
            label={t('新しいパスワード')}
            description={t(
              '半角英数字を組み合わせて8文字以上で作成してください。'
            )}
            alignTop
          >
            <Controller
              name={'new_password'}
              control={control}
              render={({ field }) => (
                <InputPassword
                  {...field}
                  error={!!errors?.new_password}
                  helperText={errors?.new_password?.message}
                  fullWidth
                />
              )}
            />
          </StackItem>
        </div>
        <Stack align={'right'} borderTop>
          <MarginRightCancelButton onClick={hideChangePasswordModal} />
          <Button
            onClick={handleSubmit(async (value) =>
              onSubmit(value as UpdatePasswordParams)
                .then(() => {
                  hideChangePasswordModal()
                })
                .catch(() => {
                  // NOTE: このエラー以外は上の階層でcatchしている
                  setError('old_password', {
                    type: 'correct',
                    message: t('validation.正しいパスワードを入力してください'),
                  })
                })
            )}
            color="primary"
            variant="contained"
            disabled={submitDisabled}
          >
            {t('保存')}
          </Button>
        </Stack>
      </Modal>
    </>
  )
}

export default AdminUserSection
